import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { forEach } from 'lodash';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fetchActiveAgents } from '../../../actions/agents';
import FormProviderMC from '../../components/FormProviderMC';
import InputComponent from '../../components/InputMC';
import DropdownMC from '../../components/DropdownMC';
import DateRangeMC from '../../components/DateRangeMC';
import { fetchTags } from '../../../actions/ContactGroupActions';
import MultiSelectDropdownMC from '../../components/MultiSelectDropdownMC';
import MultiSelectDropdownGroupMC from '../../components/MultiSelectDropdownGroupMC';
import TitleMC from '../../components/TitleMC';

const FunnelFilterFields = ({
  filters,
  setFilters,
  showExportFields
}) => {
  const dispatch = useDispatch();

  const { agents } = useSelector((reduxState) => reduxState.agentsReducer);
  const { tags } = useSelector((reduxState) => reduxState.mainReducer);
  const { funnels } = useSelector((reduxState) => reduxState.funnelsReducer);

  const [agentsOptions, setAgentsOptions] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [funnelsOptions, setFunnelsOptions] = useState([]);

  const { t } = useTranslation('Funnels');

  const amountConditions = [
    { id: 'eq', label: t('filters.eq') },
    { id: 'lt', label: t('filters.lt') },
    { id: 'gt', label: t('filters.gt') }
  ];

  const leadStatusOptions = [
    { id: 'cold', label: t('filters.leadStatus.cold') },
    { id: 'warm', label: t('filters.leadStatus.warm') },
    { id: 'hot', label: t('filters.leadStatus.hot') }
  ];

  useEffect(() => {
    dispatch(fetchActiveAgents());
    dispatch(fetchTags());
  }, []);

  useEffect(() => {
    const data = [{ id: 'unassigned', value: 'nil', label: t('filters.noAgents') }];

    forEach(agents, (row) => {
      data.push({ id: row.id, label: `${row.first_name} ${row.last_name}` });
    });

    setAgentsOptions(data);
  }, [agents]);

  useEffect(() => {
    setTagsOptions(tags.map((tag) => ({ ...tag, id: tag.value })));
  }, [tags]);

  useEffect(() => {
    setFunnelsOptions(funnels.map(({ name, web_id }) => ({ label: name, id: web_id })));
  }, [funnels]);

  const handleInputChange = (name, newValue) => {
    let value = newValue;
    if (name === 'amount') {
      value = value.replace(/[^\d.]/g, '');
    }
    setFilters({ ...filters, [name]: value });
  };

  const handleAmountConditionSelect = (value) => setFilters({
    ...filters,
    amount_condition: value
  });

  const handleDateRangeSelect = ({ name, value }) => {
    setFilters({
      ...filters,
      [`start_${name}`]: value?.startDate,
      [`end_${name}`]: value?.endDate
    });
  };

  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const methods = useForm();

  return (
    <FormProviderMC methods={methods} className="tw-space-y-4">
      {showExportFields && (
        <InputComponent
          name="name"
          value={filters.name}
          secondaryLabel={t('filters.name.label')}
          placeholder={t('filters.name.placeholder')}
          onChange={handleInputChange}
          useFloatingAndNonFloatingLabel
        />
      )}
      <div>
        <TitleMC text={t('filters.amount.title')} variant="SECTION" />
        <div className="tw-flex tw-gap-2">
          <InputComponent
            name="amount"
            value={filters.amount}
            placeholder={t('filters.amount.placeholder')}
            containerClass="tw-w-2/3"
            type="number"
            onChange={handleInputChange}
          />
          <DropdownMC
            options={amountConditions}
            name="amount_condition"
            defaultValue={filters.amount_condition}
            onSelect={handleAmountConditionSelect}
            className="!tw-w-1/3"
          />
        </div>
      </div>

      <div>
        <TitleMC text={t('filters.created_at')} variant="SECTION" />
        <DateRangeMC
          name="created_at"
          defaultDate={{
            startDate: filters.start_created_at,
            endDate: filters.end_created_at
          }}
          handleChange={handleDateRangeSelect}
        />
      </div>

      <div>
        <TitleMC text={t('filters.expected_close_date')} variant="SECTION" />
        <DateRangeMC
          name="expected_close_date"
          defaultDate={{
            startDate: filters.start_expected_close_date,
            endDate: filters.end_expected_close_date
          }}
          handleChange={handleDateRangeSelect}
        />
      </div>

      <div>
        <TitleMC text={t('filters.agents.title')} variant="SECTION" />
        <MultiSelectDropdownMC
          name="agents"
          options={agentsOptions}
          defaultValue={filters.agents}
          onSelect={handleFilterChange}
          placeholder={t('filters.agents.placeholder')}
          search
        />
      </div>

      <div>
        <TitleMC text={t('filters.leadStatus.title')} variant="SECTION" />
        <MultiSelectDropdownMC
          name="lead_status"
          options={leadStatusOptions}
          defaultValue={filters.lead_status}
          onSelect={handleFilterChange}
          placeholder={t('filters.leadStatus.placeholder')}
        />
      </div>

      <div>
        <TitleMC text={t('filters.includedTags.title')} variant="SECTION" />
        <MultiSelectDropdownGroupMC
          name="included_tags"
          options={tagsOptions}
          defaultValue={filters.included_tags}
          onChange={handleFilterChange}
          addButtonLabel={t('filters.tags.addButton')}
          placeholder={t('filters.includedTags.placeholder')}
        />
      </div>

      <div>
        <TitleMC text={t('filters.excludedTags.title')} variant="SECTION" />
        <MultiSelectDropdownGroupMC
          name="excluded_tags"
          options={tagsOptions}
          defaultValue={filters.excluded_tags}
          onChange={handleFilterChange}
          addButtonLabel={t('filters.tags.addButton')}
          placeholder={t('filters.excludedTags.placeholder')}
        />
      </div>

      {showExportFields && (
        <div>
          <TitleMC text={t('filters.funnels.title')} variant="SECTION" />
          <MultiSelectDropdownMC
            name="funnels"
            options={funnelsOptions}
            defaultValue={filters.funnels}
            onSelect={handleFilterChange}
            placeholder={t('filters.funnels.placeholder')}
            search
          />
        </div>
      )}
    </FormProviderMC>
  );
};

export default FunnelFilterFields;
